import { type DragEvent, useState } from "react";
import { GALLERY_ASSET_DRAG_TYPE } from "./galleryTree";

/**
 * Wires drag-over / drop handlers for a target that accepts personal
 * assets being dragged to be shared with the active organization.
 *
 * Returns ``isOver`` for the allowed highlight plus three
 * dataTransfer-aware handlers as ``dropProps``. Pass ``null`` for
 * ``onShare`` to disable the target entirely (e.g. when there is no
 * active org context, so the section isn't rendered anyway, but the
 * hook can still be called safely).
 *
 * There's no ``isForbidden`` state because asset drags only originate
 * from *personal* cards in the first place — org-shared cards are not
 * draggable, so the personal→org direction is the only one possible.
 * If someone drops an asset onto a non-org target, the target just
 * doesn't preventDefault and the browser drops it as a no-op.
 */
export function useAssetDropTarget<T extends HTMLElement>(
  onShare: ((assetId: string) => void | Promise<void>) | null | undefined,
) {
  const [isOver, setIsOver] = useState(false);
  const enabled = Boolean(onShare);

  const onDragOver = (event: DragEvent<T>) => {
    if (
      !(enabled && event.dataTransfer.types.includes(GALLERY_ASSET_DRAG_TYPE))
    ) {
      return;
    }
    // Stop bubbling so a parent drop target doesn't also light up while
    // we're already hovering a more specific child target.
    event.stopPropagation();
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setIsOver(true);
  };

  const onDragLeave = () => {
    setIsOver(false);
  };

  const onDrop = async (event: DragEvent<T>) => {
    if (!(enabled && onShare)) return;
    event.stopPropagation();
    event.preventDefault();
    setIsOver(false);
    const assetId = event.dataTransfer.getData(GALLERY_ASSET_DRAG_TYPE);
    if (!assetId) return;
    await onShare(assetId);
  };

  return {
    isOver,
    dropProps: { onDragOver, onDragLeave, onDrop },
  };
}
